import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Drawer,
  Group,
  Progress,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import { lazy, Suspense } from "react";
import { ownerApi, type LegalDocRow } from "../api/client";

// Heavy: brings recharts/react-markdown via ShopConversationPage's transitive
// imports. Lazy-loaded so opening the drawer doesn't pull it on first paint.
const ConversationDetailContent = lazy(() =>
  import("../pages/ShopConversationPage").then((m) => ({ default: m.ConversationDetailContent }))
);
import {
  FetchError,
  PANEL_STYLE,
  SignedPill,
  T,
  dollars,
  fmtDateTime,
  pct,
} from "../components/ownerUi";

interface QuoteItem {
  source_id: string | null;
  label: string;
  amount_cents: number;
  item_status: "unauthorized" | "selected" | "approved" | "declined" | null;
}

interface SummaryRow {
  conversation_id: number;
  shop_id: number;
  shop_name: string;
  updated_at: string;
  lead_source: string | null;
  customer_name: string;
  vehicle: string;
  charger_names: string[];
  total_cents: number;
  items_quoted: number;
  items_approved: number;
  items_declined: number;
  cents_quoted: number;
  cents_approved: number;
  cents_declined: number;
  signed_status: string;
  signature_method: string | null;
  hmac_valid: boolean | null;
  quote_items: QuoteItem[];
}

interface ConversationDrawerCtx {
  open: (conversationId: number, shopHint?: number) => void;
  close: () => void;
}

const Ctx = createContext<ConversationDrawerCtx | null>(null);

export function useConversationDrawer(): ConversationDrawerCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useConversationDrawer must be used within ConversationDrawerProvider");
  return v;
}

function DrawerHeader({
  loading, summary, onOpenFull,
}: {
  loading: boolean;
  summary: SummaryRow | null;
  onOpenFull?: () => void;
}) {
  if (loading || !summary) {
    return (
      <Box>
        <Skeleton h={10} w={140} mb={6} />
        <Skeleton h={20} w={220} mb={4} />
        <Skeleton h={12} w={180} />
      </Box>
    );
  }
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Box style={{ minWidth: 0, flex: 1 }}>
        <Text size="11px" fw={700} c={T.textMuted} tt="uppercase" style={{ letterSpacing: "0.04em" }}>
          Conversation · {summary.shop_name}
        </Text>
        <Text size="lg" fw={600} mt={2} truncate>{summary.customer_name}</Text>
        <Text size="xs" c={T.textMuted} truncate>{summary.vehicle}</Text>
      </Box>
      {onOpenFull && (
        <Tooltip label="Open full conversation" position="bottom" withinPortal>
          <ActionIcon variant="default" size="md" onClick={onOpenFull} aria-label="Open full">
            <IconExternalLink size={14} stroke={1.75} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
}

function DrawerSkeleton() {
  return (
    <Stack gap="md" mt="md">
      <Skeleton h={16} w="70%" />
      <Skeleton h={16} w="60%" />
      <Skeleton h={16} w="80%" />
      <Skeleton h={40} mt="md" />
    </Stack>
  );
}

function itemStatusPill(status: string | null) {
  const map: Record<string, { label: string; bg: string; fg: string }> = {
    approved: { label: "Approved", bg: "rgba(22,163,74,0.12)", fg: T.success },
    declined: { label: "Declined", bg: "rgba(220,38,38,0.12)", fg: T.danger },
    selected: { label: "Selected", bg: "rgba(99,102,241,0.16)", fg: "#6366F1" },
    unauthorized: { label: "Unauthorized", bg: T.bgSubtle, fg: T.textMuted },
  };
  const s = (status && map[status]) || { label: "—", bg: T.bgSubtle, fg: T.textFaint };
  return (
    <Box
      component="span"
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "1px 8px",
        borderRadius: 4,
        background: s.bg,
        color: s.fg,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </Box>
  );
}

// The Repair / Inspection / Quote tabs are hoisted out of ConversationDetailContent
// into the drawer's top-level bar; Legal docs uses the drawer's own (richer) panel.
const CONTENT_TABS = ["chat", "inspection", "quote"];

export function ConversationDrawerProvider({ children }: { children: ReactNode }) {
  const [openedId, setOpenedId] = useState<number | null>(null);
  const [shopHint, setShopHint] = useState<number | null>(null);
  const [summary, setSummary] = useState<SummaryRow | null>(null);
  const [legal, setLegal] = useState<LegalDocRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [tab, setTab] = useState("overview");
  const [contentMounted, setContentMounted] = useState(false);

  const open = useCallback((conversationId: number, shop?: number) => {
    setOpenedId(conversationId);
    setShopHint(shop ?? null);
  }, []);
  const close = useCallback(() => setOpenedId(null), []);

  const fetchConv = useCallback(() => {
    if (openedId === null) return;
    setLoading(true);
    setError(null);
    ownerApi
      .conversation(openedId)
      .then((r) => {
        setSummary(r.summary);
        setLegal(r.legal_docs);
      })
      .catch((e: Error) => {
        setError(e);
        setSummary(null);
        setLegal([]);
      })
      .finally(() => setLoading(false));
  }, [openedId]);

  useEffect(() => {
    if (openedId === null) {
      setSummary(null);
      setLegal(null);
      setError(null);
      return;
    }
    fetchConv();
  }, [openedId, fetchConv]);

  // Reset to Overview and drop the lazy embedded content when a different
  // conversation opens; mount that content the first time a content tab shows.
  useEffect(() => { setTab("overview"); setContentMounted(false); }, [openedId]);
  useEffect(() => { if (CONTENT_TABS.includes(tab)) setContentMounted(true); }, [tab]);

  // "Open full conversation" used to navigate to a separate page; the
  // drawer's Overview / Approval / Legal docs tabs cover the same
  // surface, so we removed the external-link nav per the brief
  // "Open Full Conversation merge to same drawer." The full page is
  // still reachable from the per-shop conversation list if needed.
  const openFull = undefined;

  return (
    <Ctx.Provider value={{ open, close }}>
      {children}
      <Drawer
        opened={openedId !== null}
        onClose={close}
        position="right"
        size="xl"
        padding="lg"
        closeOnEscape
        title={<DrawerHeader loading={loading} summary={summary} onOpenFull={openFull} />}
        styles={{ header: { position: "sticky", top: 0, background: "white", zIndex: 10, borderBottom: `1px solid ${T.border}` } }}
      >
        {error ? (
          <Box mt="md"><FetchError message="Couldn't load this conversation." onRetry={fetchConv} /></Box>
        ) : loading || !summary ? (
          <DrawerSkeleton />
        ) : (
          <>
          <Tabs value={tab} onChange={(v) => setTab(v || "overview")} mt="sm">
            <Tabs.List>
              <Tabs.Tab value="overview">Overview</Tabs.Tab>
              <Tabs.Tab value="chat">Repair</Tabs.Tab>
              <Tabs.Tab value="inspection">Inspection</Tabs.Tab>
              <Tabs.Tab value="quote">Quote</Tabs.Tab>
              <Tabs.Tab value="approval">
                Approval
                {(summary.items_approved + summary.items_declined) > 0 && (
                  <Badge size="xs" variant="light" color="indigo" ml={6}>
                    {pct(summary.items_approved, summary.items_approved + summary.items_declined)}%
                  </Badge>
                )}
              </Tabs.Tab>
              <Tabs.Tab value="legal">
                Legal docs
                {legal && legal.length > 0 && (
                  <Badge size="xs" variant="light" color="green" ml={6}>{legal.length}</Badge>
                )}
              </Tabs.Tab>
            </Tabs.List>

            {/* Overview tab */}
            <Tabs.Panel value="overview" pt="md">
              <Stack gap="xs">
                {[
                  ["Shop", summary.shop_name],
                  ["Updated", fmtDateTime(summary.updated_at)],
                  ["Total", summary.cents_quoted ? dollars(summary.cents_quoted) : "—"],
                  ["Tech(s)", summary.charger_names.length ? summary.charger_names.join(", ") : "—"],
                  ...(summary.lead_source ? [["Lead source", summary.lead_source]] : []),
                ].map(([k, v]) => (
                  <Group key={k} gap={12} align="center">
                    <Text size="xs" c={T.textMuted} w={80} tt="uppercase" fw={600} style={{ letterSpacing: "0.04em" }}>{k}</Text>
                    <Text size="sm">{v}</Text>
                  </Group>
                ))}
                <Group gap={12} align="center">
                  <Text size="xs" c={T.textMuted} w={80} tt="uppercase" fw={600} style={{ letterSpacing: "0.04em" }}>Status</Text>
                  <SignedPill status={summary.signed_status} method={summary.signature_method} />
                </Group>
              </Stack>
            </Tabs.Panel>

            {/* Approval tab — with per-item breakdown */}
            <Tabs.Panel value="approval" pt="md">
              <Stack gap="md">
                <Box p="md" style={PANEL_STYLE}>
                  <Group justify="space-between" mb={4}>
                    <Text size="11px" fw={700} c={T.textMuted} tt="uppercase" style={{ letterSpacing: "0.04em" }}>
                      Items approved
                    </Text>
                    <Text size="xs" c={T.textMuted}>
                      {summary.items_approved}/{summary.items_approved + summary.items_declined} decided
                    </Text>
                  </Group>
                  <Group gap={6} align="baseline">
                    <Text size="22px" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                      {pct(summary.items_approved, summary.items_approved + summary.items_declined)}%
                    </Text>
                  </Group>
                  <Progress
                    mt={6}
                    size="xs"
                    value={pct(summary.items_approved, summary.items_approved + summary.items_declined)}
                    color="indigo"
                  />
                </Box>
                <Box p="md" style={PANEL_STYLE}>
                  <Group justify="space-between" mb={4}>
                    <Text size="11px" fw={700} c={T.textMuted} tt="uppercase" style={{ letterSpacing: "0.04em" }}>
                      Dollars approved
                    </Text>
                    <Text size="xs" c={T.textMuted}>
                      {dollars(summary.cents_approved)} / {dollars(summary.cents_approved + summary.cents_declined)}
                    </Text>
                  </Group>
                  <Group gap={6} align="baseline">
                    <Text size="22px" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                      {pct(summary.cents_approved, summary.cents_approved + summary.cents_declined)}%
                    </Text>
                  </Group>
                  <Progress
                    mt={6}
                    size="xs"
                    value={pct(summary.cents_approved, summary.cents_approved + summary.cents_declined)}
                    color="green"
                  />
                </Box>

                {summary.quote_items.length > 0 && (
                  <Box style={PANEL_STYLE}>
                    <Box p="md" pb={6}>
                      <Text size="11px" fw={700} c={T.textMuted} tt="uppercase" style={{ letterSpacing: "0.04em" }}>
                        Line items
                      </Text>
                    </Box>
                    <Stack gap={0} px="md" pb="md">
                      {summary.quote_items.map((it, idx) => (
                        <Group
                          key={`${it.source_id ?? "noid"}-${idx}`}
                          gap={8}
                          wrap="nowrap"
                          py={8}
                          style={{
                            borderTop: idx > 0 ? `1px solid ${T.border}` : undefined,
                          }}
                        >
                          <Text size="13px" style={{ flex: 1, minWidth: 0 }} truncate>{it.label}</Text>
                          <Text
                            size="12px"
                            c={T.textMuted}
                            style={{ fontVariantNumeric: "tabular-nums", minWidth: 60, textAlign: "right" }}
                          >
                            {it.amount_cents ? dollars(it.amount_cents) : "—"}
                          </Text>
                          {itemStatusPill(it.item_status)}
                        </Group>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Tabs.Panel>

            {/* Legal docs tab */}
            <Tabs.Panel value="legal" pt="md">
              {!legal ? (
                <DrawerSkeleton />
              ) : legal.length === 0 ? (
                <Text size="sm" c={T.textMuted} ta="center" py="xl">No legal documents recorded.</Text>
              ) : (
                <Stack gap="xs">
                  {legal.map((d, i) => <LegalDocCard key={i} d={d} />)}
                </Stack>
              )}
            </Tabs.Panel>
          </Tabs>

          {/* Repair / Inspection / Quote content, hoisted from
              ConversationDetailContent into the bar above. Lazy-mounted on the
              first content-tab click, then kept mounted so switching between
              them doesn't refetch. */}
          {contentMounted && (
            <Box style={{ display: CONTENT_TABS.includes(tab) ? "block" : "none", minHeight: 500 }}>
              <Suspense fallback={<DrawerSkeleton />}>
                <ConversationDetailContent
                  shopId={summary.shop_id}
                  conversationId={summary.conversation_id}
                  controlledTab={CONTENT_TABS.includes(tab) ? tab : "chat"}
                  chargerNames={summary.charger_names}
                />
              </Suspense>
            </Box>
          )}
          </>
        )}
      </Drawer>
    </Ctx.Provider>
  );
}

// One legal-doc card. Open / Download link straight to the signed document;
// no inline preview embed (kept the drawer light and avoided the heavy
// full-page PDF/image embed).
function LegalDocCard({ d }: { d: LegalDocRow }) {
  const docUrl = d.pdf_url || d.file_url || null;
  return (
    <Box p="sm" style={PANEL_STYLE}>
      <Group gap={6} mb={4}>
        <Badge size="sm" variant="light" color={d.kind === "tek_authorization" ? "violet" : "indigo"}>
          {d.kind.replace("_", " ")}
        </Badge>
        <Badge size="sm" variant="outline">
          {d.method === "paper_photo" ? "Paper" : d.method === "live" ? "Online" : (d.method_detail || d.method)}
        </Badge>
        {d.hmac_valid === true && <Badge size="sm" color="green" variant="light">HMAC ✓</Badge>}
        {d.hmac_valid === false && <Badge size="sm" color="red" variant="light">Tampered</Badge>}
        {d.hmac_valid === null && <Badge size="sm" color="gray" variant="light">Legacy</Badge>}
      </Group>
      {d.authorizer_name && (
        <Text size="sm">{d.method === "shop_approved" ? "Shop-approved by " : "Signed by "}<b>{d.authorizer_name}</b></Text>
      )}
      <Text size="xs" c={T.textMuted} mt={2}>
        {d.signed_at ? fmtDateTime(d.signed_at) : "—"}
      </Text>
      {d.kind === "tek_authorization" && (d.ro_total_cents != null || d.ip) && (
        <Group gap={12} mt={2}>
          {d.ro_total_cents != null && <Text size="xs" c={T.textMuted}>{`RO total $${((d.ro_total_cents ?? 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</Text>}
          {d.ip && <Text size="xs" c={T.textMuted}>{`IP ${d.ip}`}</Text>}
        </Group>
      )}
      {docUrl ? (
        <Group gap={12} mt={6} wrap="nowrap">
          <Anchor href={docUrl} target="_blank" rel="noopener noreferrer" size="sm">Open in new tab ↗</Anchor>
          <Anchor href={docUrl} download size="sm">Download</Anchor>
        </Group>
      ) : (
        <Text size="xs" c="red" mt={6}>No signed document on file — unsigned authorization shell.</Text>
      )}
    </Box>
  );
}
